import React from 'react';
import clsx from 'clsx';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import CircularProgress from '@material-ui/core/CircularProgress';
import Slide from '@material-ui/core/Slide';
import Grid from '@material-ui/core/Grid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { fade, makeStyles, useTheme } from '@material-ui/core/styles';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import PermIdentityIcon from '@material-ui/icons/PermIdentity';
import ScheduleIcon from '@material-ui/icons/Schedule';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import red from '@material-ui/core/colors/red';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MuiPhoneNumber from 'material-ui-phone-number';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import { MY_BANKS, REST_DAYS, REST_REPEAT_OPTIONS, ADMIN_POSITION_PROMOTER } from '../../../Helper/Constants';

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const normalizeTime = (val) => {
	if (!val) return '';
	var s = String(val);
	if (s.length >= 5) return s.substring(0, 5);
	return s;
};

const formatShiftLabel = (s) => {
	if (!s) return '';
	var st = normalizeTime(s.start_time);
	var et = normalizeTime(s.end_time);
	var range = (st || et) ? ' (' + st + ' - ' + et + ')' : '';
	return (s.shift_name || '') + range;
};

const useStyles = makeStyles((theme) => {
	const isDark = theme.palette.type === 'dark';
	const line = fade(theme.palette.text.primary, 0.12);
	const line2 = fade(theme.palette.text.primary, isDark ? 0.08 : 0.07);
	const soft = fade(theme.palette.text.primary, isDark ? 0.07 : 0.05);
	return {
		paper: {
			borderRadius: 16,
			overflow: 'hidden',
			border: `1px solid ${line2}`,
			[theme.breakpoints.down('xs')]: {
				borderRadius: 0,
				border: 0
			}
		},
		header: {
			display: 'flex',
			alignItems: 'center',
			gap: 12,
			padding: theme.spacing(2, 2.5),
			borderBottom: `1px solid ${line}`,
			background: isDark
				? 'linear-gradient(135deg, rgba(254,242,0,0.08) 0%, rgba(254,242,0,0.02) 60%, rgba(255,255,255,0.02) 100%)'
				: 'linear-gradient(135deg, rgba(254,242,0,0.18) 0%, rgba(254,242,0,0.05) 60%, rgba(255,255,255,0.9) 100%)'
		},
		iconTile: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: 40,
			height: 40,
			flex: 'none',
			borderRadius: 10,
			backgroundColor: theme.palette.primary.main,
			color: '#111',
			'& svg': { fontSize: 22 }
		},
		titleWrap: {
			flex: 1,
			minWidth: 0,
			display: 'flex',
			flexDirection: 'column',
			gap: 2
		},
		title: {
			fontSize: 18,
			fontWeight: 700,
			letterSpacing: '-0.01em',
			lineHeight: 1.2
		},
		subtitle: {
			fontSize: 13,
			color: theme.palette.text.secondary,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis'
		},
		closeButton: {
			width: 36,
			height: 36,
			borderRadius: 10,
			color: theme.palette.text.secondary,
			transition: theme.transitions.create(['background-color', 'color', 'transform'], { duration: 150 }),
			'&:hover': { backgroundColor: soft, color: theme.palette.text.primary },
			'&:active': { transform: 'scale(0.94)' },
			'& .MuiIconButton-label svg': { display: 'block', fontSize: 20 }
		},
		content: {
			padding: theme.spacing(2.5),
			display: 'flex',
			flexDirection: 'column',
			gap: theme.spacing(2),
			backgroundColor: theme.palette.background.default,
			[theme.breakpoints.down('xs')]: {
				padding: theme.spacing(2)
			}
		},
		section: {
			borderRadius: 12,
			border: `1px solid ${line2}`,
			backgroundColor: theme.palette.background.paper,
			padding: theme.spacing(2),
			transition: theme.transitions.create('box-shadow', { duration: 200 }),
			'&:focus-within': {
				boxShadow: `0 0 0 1px ${fade(theme.palette.primary.main, isDark ? 0.5 : 0.7)}`
			}
		},
		sectionHead: {
			display: 'flex',
			alignItems: 'center',
			gap: 10,
			marginBottom: theme.spacing(1.75)
		},
		sectionIcon: {
			width: 28,
			height: 28,
			flex: 'none',
			borderRadius: 8,
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: fade(theme.palette.primary.main, isDark ? 0.18 : 0.4),
			color: isDark ? theme.palette.primary.main : '#6b6400',
			'& svg': { fontSize: 16 }
		},
		sectionTitle: {
			fontSize: 13,
			fontWeight: 700,
			lineHeight: 1.2
		},
		sectionHint: {
			marginLeft: 'auto',
			fontSize: 10,
			fontWeight: 700,
			letterSpacing: '0.08em',
			textTransform: 'uppercase',
			color: theme.palette.text.disabled
		},
		field: {
			'& .MuiOutlinedInput-root': {
				borderRadius: 10
			},
			'& .MuiOutlinedInput-notchedOutline': {
				transition: 'border-color 150ms'
			}
		},
		required: {
			color: isDark ? red[300] : red[600],
			marginLeft: 2
		},
		label: {
			display: 'block',
			fontSize: 12,
			fontWeight: 600,
			color: theme.palette.text.secondary,
			marginBottom: 8
		},
		restDays: {
			display: 'flex',
			flexWrap: 'wrap',
			gap: 6
		},
		dayChip: {
			height: 30,
			minWidth: 44,
			padding: '0 12px',
			borderRadius: 999,
			fontSize: 12,
			fontWeight: 600,
			fontFamily: 'inherit',
			border: `1px solid ${line}`,
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.background.paper,
			transition: theme.transitions.create(['background-color', 'border-color', 'color', 'transform'], { duration: 150 }),
			'&:hover': { backgroundColor: soft },
			'&:active': { transform: 'scale(0.95)' },
			'&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 }
		},
		dayChipOn: {
			backgroundColor: theme.palette.primary.main,
			borderColor: theme.palette.primary.main,
			color: '#111',
			'&:hover': { backgroundColor: theme.palette.primary.dark }
		},
		repeatRow: {
			display: 'flex',
			alignItems: 'center',
			gap: 8,
			flexWrap: 'wrap',
			minHeight: 40
		},
		repeatText: {
			fontSize: 13,
			color: theme.palette.text.primary
		},
		repeatSelect: {
			minWidth: 72
		},
		collapseInner: {
			paddingTop: theme.spacing(2)
		},
		footer: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'flex-end',
			gap: 8,
			padding: theme.spacing(1.5, 2.5),
			borderTop: `1px solid ${line}`,
			backgroundColor: theme.palette.background.paper
		},
		cancelButton: {
			height: 40,
			padding: '0 16px',
			borderRadius: 10,
			textTransform: 'none',
			fontWeight: 600,
			fontSize: 13
		},
		saveButton: {
			height: 40,
			minWidth: 112,
			padding: '0 20px',
			borderRadius: 10,
			textTransform: 'none',
			fontWeight: 700,
			fontSize: 13,
			boxShadow: 'none',
			transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], { duration: 150 }),
			'&:hover': { boxShadow: `0 6px 16px ${fade(theme.palette.primary.main, 0.45)}` },
			'&:active': { transform: 'scale(0.97)' }
		}
	};
});

function Section({ classes, icon, title, hint, children }) {
	return (
		<div className={classes.section}>
			<div className={classes.sectionHead}>
				<span className={classes.sectionIcon} aria-hidden="true">{icon}</span>
				<Typography component="h2" className={classes.sectionTitle}>{title}</Typography>
				{hint ? <span className={classes.sectionHint}>{hint}</span> : null}
			</div>
			{children}
		</div>
	);
}

export default function UserFormDialog({
	open,
	mode,
	language,
	form,
	errors,
	onFieldChange,
	onToggleRestDay,
	roleList,
	shiftList,
	countryIso2,
	workingShiftEnabled,
	showPassword,
	onToggleShowPassword,
	onClose,
	onSubmit,
	submitting,
	canSubmit
}) {
	const classes = useStyles();
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
	const isEdit = mode === 'edit';
	const fieldErrors = errors || {};

	const requiredLabel = (text) => (
		<span>{text}<span className={classes.required}>*</span></span>
	);

	return (
		<Dialog
			open={open}
			onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }}
			maxWidth="md"
			fullWidth
			fullScreen={fullScreen}
			scroll="paper"
			TransitionComponent={SlideUp}
			PaperProps={{ className: classes.paper }}
			aria-labelledby="user-form-dialog-title"
		>
			<div className={classes.header}>
				<span className={classes.iconTile} aria-hidden="true">
					{isEdit ? <EditIcon /> : <PersonAddIcon />}
				</span>
				<div className={classes.titleWrap}>
					<Typography component="h1" id="user-form-dialog-title" className={classes.title}>
						{isEdit ? language.edit_user : language.create_user}
					</Typography>
					{isEdit && form.username ? (
						<span className={classes.subtitle}>@{form.username}</span>
					) : null}
				</div>
				<IconButton className={classes.closeButton} aria-label={language.close} onClick={onClose}>
					<CloseIcon />
				</IconButton>
			</div>

			<DialogContent className={classes.content}>
				<Section classes={classes} icon={<LockOutlinedIcon />} title={language.account_info}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<TextField
								className={classes.field}
								fullWidth
								label={isEdit ? language.username : requiredLabel(language.username)}
								value={form.username}
								onChange={(e) => onFieldChange('username', e.target.value)}
								disabled={isEdit}
								error={!isEdit && !!fieldErrors.username}
								helperText={!isEdit && fieldErrors.username ? language.username_error : ''}
								size="small"
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControl variant="outlined" size="small" fullWidth className={classes.field}>
								<InputLabel>{requiredLabel(language.select_role)}</InputLabel>
								<Select
									label={language.select_role + ' *'}
									value={form.role_id}
									onChange={(e) => onFieldChange('role_id', e.target.value)}
								>
									<MenuItem value=""><em>-</em></MenuItem>
									{roleList.map((r) => (
										<MenuItem key={(isEdit ? 'er-' : 'nr-') + r.role_id} value={isEdit ? String(r.role_id) : r.role_id}>
											{r.role_id === ADMIN_POSITION_PROMOTER ? language.promoter : r.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								className={classes.field}
								fullWidth
								label={requiredLabel(language.name_label)}
								value={form.name}
								onChange={(e) => onFieldChange('name', e.target.value)}
								size="small"
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								className={classes.field}
								fullWidth
								label={isEdit ? language.new_pw : requiredLabel(language.password)}
								type={showPassword ? 'text' : 'password'}
								value={form.password}
								onChange={(e) => onFieldChange('password', e.target.value)}
								error={!isEdit && !!fieldErrors.password}
								helperText={!isEdit && fieldErrors.password ? language.password_error : ''}
								size="small"
								variant="outlined"
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton size="small" onClick={onToggleShowPassword}>
												{showPassword ? <Visibility /> : <VisibilityOff />}
											</IconButton>
										</InputAdornment>
									)
								}}
							/>
						</Grid>
					</Grid>
				</Section>

				<Section classes={classes} icon={<PermIdentityIcon />} title={language.personal} hint={language.optional}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<TextField
								className={classes.field}
								fullWidth
								label={language.ic_no}
								value={form.ic_no}
								onChange={(e) => onFieldChange('ic_no', e.target.value)}
								size="small"
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<MuiPhoneNumber
								className={classes.field}
								fullWidth
								name="phone"
								label={language.phone_number}
								value={form.phone_number}
								onChange={(prop) => onFieldChange('phone_number', prop)}
								key={countryIso2}
								defaultCountry={countryIso2}
								countryCodeEditable={false}
								regions={['asia']}
								size="small"
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								className={classes.field}
								fullWidth
								label={language.address}
								value={form.address}
								onChange={(e) => onFieldChange('address', e.target.value)}
								size="small"
								variant="outlined"
								multiline
								rows={2}
							/>
						</Grid>
					</Grid>
				</Section>

				<Section classes={classes} icon={<ScheduleIcon />} title={language.working_time} hint={language.optional}>
					<Grid container spacing={2}>
						{workingShiftEnabled && (
							<Grid item xs={12} sm={6}>
								<FormControl variant="outlined" size="small" fullWidth className={classes.field}>
									<InputLabel>{language.working_shift}</InputLabel>
									<Select
										label={language.working_shift}
										value={form.shift_id || ''}
										onChange={(e) => onFieldChange('shift_id', e.target.value)}
									>
										<MenuItem value=""><em>-</em></MenuItem>
										{shiftList.map((s) => (
											<MenuItem key={'sh-' + s.id} value={String(s.id)}>{formatShiftLabel(s)}</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
						)}
						<Grid item xs={12} sm={6}>
							<div className={classes.repeatRow}>
								<span className={classes.repeatText}>{language.repeats_every}</span>
								<FormControl variant="outlined" size="small" className={clsx(classes.field, classes.repeatSelect)}>
									<Select
										value={form.rest_repeat_weeks}
										onChange={(e) => onFieldChange('rest_repeat_weeks', e.target.value)}
										displayEmpty
									>
										<MenuItem value=""><em>-</em></MenuItem>
										{REST_REPEAT_OPTIONS.map((n) => (
											<MenuItem key={'rr-' + n} value={n}>{n}</MenuItem>
										))}
									</Select>
								</FormControl>
								<span className={classes.repeatText}>{language.week}</span>
							</div>
						</Grid>
						<Grid item xs={12}>
							<span className={classes.label}>{language.select_rest_day}</span>
							<div className={classes.restDays} role="group" aria-label={language.select_rest_day}>
								{REST_DAYS.map((d) => {
									var selected = (form.rest_days || []).includes(d.value);
									return (
										<ButtonBase
											key={'rd-' + d.value}
											className={clsx(classes.dayChip, selected && classes.dayChipOn)}
											aria-pressed={selected}
											onClick={() => onToggleRestDay(d.value)}
										>
											{language[d.labelKey]}
										</ButtonBase>
									);
								})}
							</div>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Collapse in={!!form.rest_repeat_weeks} unmountOnExit>
								<MuiPickersUtilsProvider utils={DateFnsUtils}>
									<DatePicker
										className={classes.field}
										fullWidth
										views={['year', 'date']}
										format="dd-MM-yyyy"
										label={language.rest_start_date}
										inputVariant="outlined"
										size="small"
										value={form.rest_start_date ? moment(form.rest_start_date, 'YYYY-MM-DD').toDate() : null}
										onChange={(date) => onFieldChange('rest_start_date', date ? moment(date).format('YYYY-MM-DD') : '')}
									/>
								</MuiPickersUtilsProvider>
							</Collapse>
						</Grid>
					</Grid>
				</Section>

				<Section classes={classes} icon={<AccountBalanceIcon />} title={language.bank_details} hint={language.optional}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<FormControl variant="outlined" size="small" fullWidth className={classes.field}>
								<InputLabel>{language.bank_name}</InputLabel>
								<Select
									label={language.bank_name}
									value={form.bank_name}
									onChange={(e) => onFieldChange('bank_name', e.target.value)}
								>
									<MenuItem value=""><em>-</em></MenuItem>
									{MY_BANKS.map((b) => (
										<MenuItem key={'bk-' + b} value={b}>{b}</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								className={classes.field}
								fullWidth
								label={language.bank_account_name}
								value={form.bank_account_name}
								onChange={(e) => onFieldChange('bank_account_name', e.target.value)}
								size="small"
								variant="outlined"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								className={classes.field}
								fullWidth
								label={language.bank_account_number}
								value={form.bank_account_no}
								onChange={(e) => onFieldChange('bank_account_no', e.target.value)}
								size="small"
								variant="outlined"
							/>
						</Grid>
					</Grid>
				</Section>
			</DialogContent>

			<DialogActions className={classes.footer}>
				<Button className={classes.cancelButton} variant="outlined" color="primary" onClick={onClose}>
					{language.cancel}
				</Button>
				<Button
					className={classes.saveButton}
					variant="contained"
					color="primary"
					onClick={onSubmit}
					disabled={!canSubmit || submitting}
				>
					{submitting
						? <CircularProgress size={20} color="inherit" />
						: language.save
					}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
